import { App, DatePicker, Form, type FormInstance, Modal, Select } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { recordConsent } from "../api/consent.ts"
import { announceConsentChange } from "./consent-events.ts"
import type { Channel, ContactRef } from "../types/compose.types.ts"
import {
  CHANNEL_REFUSAL,
  type ConsentState,
  type ContactConsent
} from "../types/consent.types.ts"

export interface ChangeForm {
  state: ConsentState
  reason: string
  date: Dayjs
}

export const consentFieldErrors = (data: unknown) => {
  const body = (data ?? {}) as { details?: Record<string, unknown> } & Record<
    string,
    unknown
  >
  const details = (
    body.details && typeof body.details === "object" ? body.details : body
  ) as Record<string, unknown>
  return Object.entries(details)
    .filter(([, value]) => Array.isArray(value))
    .map(([name, value]) => ({ name, errors: value as string[] }))
}

/** The states a channel can be moved to: allowed, or that channel's refusal. */
export const consentStatesFor = (channel: Channel): ConsentState[] => [
  "allowed",
  CHANNEL_REFUSAL[channel]
]

export const initialChange = (
  channel: Channel,
  standing: ConsentState
): ChangeForm => ({
  state: standing === "allowed" ? CHANNEL_REFUSAL[channel] : "allowed",
  reason: "",
  date: dayjs()
})

/**
 * Records one change through the consent endpoint; field errors land on the
 * form, anything else is reported as `failed`.
 */
export const useConsentSave = ({
  contact,
  channel,
  form,
  onSaved
}: {
  contact: ContactRef
  channel: Channel | null
  form: FormInstance<ChangeForm>
  onSaved: (consent: ContactConsent) => void
}) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const save = async (values: ChangeForm) => {
    if (!channel) return
    setSaving(true)
    setFailed(false)
    const response = await recordConsent(contact, {
      channel,
      state: values.state,
      reason: values.reason.trim(),
      date: values.date.format("YYYY-MM-DD")
    })
    setSaving(false)
    if (!response.ok) {
      const errors = consentFieldErrors(response.data).filter((error) =>
        ["state", "reason", "date"].includes(error.name)
      )
      form.setFields(errors as never)
      if (!errors.length) {
        setFailed(true)
        message.error(t("communication.consent.saveFailed"))
      }
      return
    }
    onSaved(unwrapEnvelope<ContactConsent>(response.data))
    announceConsentChange(contact)
    message.success(t("communication.consent.saved"))
  }

  return { save, saving, failed }
}

/** The new state, the reason and the day it was asked: every field the endpoint stores. */
export const ConsentChangeFields = ({
  channel,
  stateLabel,
  dateLabel,
  reasonInput,
  optionLabel,
  autoFocus = false
}: {
  channel: Channel
  stateLabel?: string
  dateLabel?: string
  reasonInput?: React.ReactNode
  optionLabel?: (state: ConsentState) => string
  autoFocus?: boolean
}) => {
  const { t } = useTranslation()
  const stateText = stateLabel ?? t("communication.consent.newState")
  const dateText = dateLabel ?? t("communication.consent.date")
  return (
    <>
      <Form.Item name="state" label={stateText} rules={[{ required: true }]}>
        <Select
          aria-label={stateText}
          autoFocus={autoFocus}
          options={consentStatesFor(channel).map((value) => ({
            value,
            label: optionLabel
              ? optionLabel(value)
              : t(`communication.consent.state.${value}`)
          }))}
        />
      </Form.Item>
      <Form.Item
        name="date"
        label={dateText}
        rules={[
          {
            required: true,
            message: t("communication.consent.dateRequired")
          }
        ]}
      >
        <DatePicker
          aria-label={dateText}
          className="w-full"
          disabledDate={(day) => day.isAfter(dayjs(), "day")}
        />
      </Form.Item>
      <Form.Item
        name="reason"
        label={t("communication.consent.reason")}
        rules={[
          {
            required: true,
            whitespace: true,
            message: t("communication.consent.reasonRequired")
          }
        ]}
      >
        {reasonInput ?? (
          <TextInput
            placeholder={t("communication.consent.reasonPlaceholder")}
            maxLength={255}
          />
        )}
      </Form.Item>
    </>
  )
}

/**
 * Records an evidence-backed consent change: a required reason and the date it
 * was asked. The one way any screen moves a contact's consent.
 */
export const ConsentChangeModal = ({
  contact,
  channel,
  standing,
  onClose,
  onSaved
}: {
  contact: ContactRef
  channel: Channel | null
  standing: ConsentState
  onClose: () => void
  onSaved: (consent: ContactConsent) => void
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<ChangeForm>()
  const { save, saving } = useConsentSave({ contact, channel, form, onSaved })

  return (
    <Modal
      open={channel !== null}
      title={
        channel
          ? t("communication.consent.change", {
              channel: t(`communication.templates.channel.${channel}`)
            })
          : undefined
      }
      okText={t("communication.consent.record")}
      cancelText={t("communication.bulk.cancel")}
      confirmLoading={saving}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form<ChangeForm>
        form={form}
        layout="vertical"
        onFinish={save}
        preserve={false}
        initialValues={channel ? initialChange(channel, standing) : undefined}
      >
        {channel && <ConsentChangeFields channel={channel} />}
      </Form>
    </Modal>
  )
}
